import { useState } from 'react';
import { CellContext } from '@tanstack/react-table';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { Button } from '@round/whitelabel-ui';
import { CampaignTableRow } from '../ProjectCampaignsTable';
import BudgetCell from 'Modules/Plans/Campaigns/components/CampaignsTable/components/BudgetCell/BudgetCell';
import PaymentStatusBadge from 'Modules/Plans/Finance/components/PaymentStatusBadge/PaymentStatusBadge';
import CampaignPaymentRequestModal from '../CampaignPaymentRequestModal/CampaignPaymentRequestModal';
import useProjectDetails from '../../../useProjectDetails';
import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import styles from './CampaignCostCell.module.css';

type Meta = { isLoading: boolean; statsLoading: number[] };
const getTableMeta = getTableMetaHelper<Meta>();

const CampaignCostCell = ({ row: { original }, table }: CellContext<CampaignTableRow, unknown>) => {
    const { isLoading, statsLoading } = getTableMeta(table);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { team, isLoading: isProjectLoading, isBrandLoading, isTeamLoading } = useProjectDetails();
    const clientId = team?.client_id;
    const { clients, status: clientsStatus } = useCurrentUserClients();
    const isPaymentEnabled = !!clients.find((client) => client.id === clientId)?.is_payment_enabled;
    const isCampaignPaymentRequestsEnabled = useFeatureFlags().isEnabled('rou_539_campaign_payment_requests');

    const isPlannerOrFinance = useCheckUserGroupsAccess(['round_planner', 'round_finance'], 'any');

    const canRequestCampaignPayment =
        !original.is_post_cost_editable &&
        original.managed_by === 'creatorbase' &&
        isPaymentEnabled &&
        isCampaignPaymentRequestsEnabled;

    const isPaymentSlotLoading =
        isCampaignPaymentRequestsEnabled &&
        (isLoading || isProjectLoading || isBrandLoading || isTeamLoading || clientsStatus === 'loading');

    const budgetCell = (
        <BudgetCell
            cost={original.cost}
            currencyId={original.currency?.id ?? null}
            amountSpent={original.currency?.id ? original.stats?.total_spent?.[original.currency.id] ?? null : null}
            spentPercentage={original.stats?.progress_percentage ?? null}
            managedBy={original.managed_by}
            isPostCostEditable={original.is_post_cost_editable}
            isLoading={isLoading || statsLoading.includes(original.id)}
        />
    );

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            {canRequestCampaignPayment ? (
                <Button
                    appearance="ghost"
                    type="button"
                    className={styles.budgetButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(true);
                    }}
                >
                    <div className={styles.costSection}>{budgetCell}</div>
                </Button>
            ) : (
                <div className={styles.costSection}>{budgetCell}</div>
            )}

            {isPaymentSlotLoading ? (
                <Skeleton width="4.5rem" height="1.5rem" borderRadius="0.25rem" />
            ) : (
                canRequestCampaignPayment && (
                    <>
                        {original.payment_status && <PaymentStatusBadge status={original.payment_status} />}

                        <CampaignPaymentRequestModal
                            clientId={clientId}
                            campaignId={original.id}
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            shouldDisablePayment={!isPlannerOrFinance}
                        />
                    </>
                )
            )}
        </div>
    );
};

export default CampaignCostCell;
